import { useState, useEffect, useMemo, useCallback } from 'react';
import { WorkflowService, sharedApiClient } from '../fetcher';
import { useNotifications, UserAbility, isAbortError } from '@medprompt/shared';

const useDocumentWorkflow = ({ documentId, doc, user, location, navigate, getDocScopeId, docScopeOptions, loadKey }) => {
  const { addNotification } = useNotifications();
  const workflowService = useMemo(() => new WorkflowService(sharedApiClient), []);

  const [workflowLogsCount, setWorkflowLogsCount] = useState(0);
  const [showWorkflowLogsModal, setShowWorkflowLogsModal] = useState(false);
  const [workflowPreviewModalOpen, setWorkflowPreviewModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const docId = doc?.documentId;
  const canReadConfig = Boolean(user?.authorities?.includes(UserAbility.CONFIG_READ.key));

  // The logs modal is deliberately not closed here: prev/next carries it across documents.
  useEffect(() => {
    setWorkflowLogsCount(0);
  }, [loadKey]);

  // Reopen workflow modals when returning from workflow detail view or navigating between documents
  useEffect(() => {
    if (!location.state?.openWorkflowLogs) return;
    console.log('Reopening workflow logs modal');
    setShowWorkflowLogsModal(true);

    const previewRuleId = location.state?.previewRuleId;
    if (previewRuleId) {
      console.log('Also reopening workflow preview for workflow:', previewRuleId);
      workflowService.fetchWorkflowRule(previewRuleId)
        .then((workflow) => {
          setSelectedWorkflow(workflow);
          setWorkflowPreviewModalOpen(true);
        })
        .catch((error) => {
          console.error('Failed to fetch workflow for preview:', error);
        });
    }
  }, [location.state, workflowService]);

  useEffect(() => {
    if (!docId || !canReadConfig) {
      setWorkflowLogsCount(0);
      return;
    }
    const requestId = getDocScopeId();
    const fetchWorkflowLogsCount = async () => {
      try {
        const logs = await workflowService.fetchWorkflowLogsForDocument(docId, docScopeOptions());
        if (requestId !== getDocScopeId()) {
          return;
        }
        setWorkflowLogsCount(Array.isArray(logs) ? logs.length : 0);
      } catch (error) {
        if (isAbortError(error) || requestId !== getDocScopeId()) return;
        console.error('Failed to fetch workflow logs:', error);
        setWorkflowLogsCount(0);
      }
    };
    fetchWorkflowLogsCount();
  }, [docId, canReadConfig, workflowService, getDocScopeId, docScopeOptions]);

  const openWorkflowLogs = useCallback(() => setShowWorkflowLogsModal(true), []);
  const closeWorkflowLogs = useCallback(() => setShowWorkflowLogsModal(false), []);

  // Fetch the workflow and show the preview on top of the logs modal
  const openWorkflowPreview = useCallback((ruleId) => {
    workflowService.fetchWorkflowRule(ruleId)
      .then((workflow) => {
        setSelectedWorkflow(workflow);
        setWorkflowPreviewModalOpen(true);
      })
      .catch((error) => {
        console.error('Failed to fetch workflow for preview:', error);
        addNotification('fetch-workflow-error', 'Failed to load workflow', { isError: true });
      });
  }, [workflowService, addNotification]);

  const closeWorkflowPreview = useCallback(() => {
    setWorkflowPreviewModalOpen(false);
    setSelectedWorkflow(null);
  }, []);

  // Navigate to workflow detail view, passing document context
  const editWorkflow = useCallback((ruleId) => {
    setWorkflowPreviewModalOpen(false);
    navigate(`/workflow/${ruleId}`, {
      state: {
        documentId,
        ruleId,
      }
    });
  }, [documentId, navigate]);

  return {
    canReadConfig,
    workflowLogsCount,
    showWorkflowLogsModal,
    openWorkflowLogs,
    closeWorkflowLogs,
    workflowPreviewModalOpen,
    selectedWorkflow,
    openWorkflowPreview,
    closeWorkflowPreview,
    editWorkflow,
  };
};

export default useDocumentWorkflow;
